import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";

import { getProjectRepository } from "../../data/projectRepository";
import type {
  ResourceRoomProjectDocumentV2,
  TimeSlot
} from "../../storage/resourceRoomDocument";
import { FiveMinuteTimeField } from "../calendar/FiveMinuteTimeField";

interface Draft {
  timeSlotId: string | null;
  dayPart: "AM" | "PM";
  start: string;
  end: string;
}

const emptyDraft: Draft = {
  timeSlotId: null,
  dayPart: "AM",
  start: "07:30",
  end: "07:55"
};

function minuteValue(clock: string): number {
  const [hour = "0", minute = "0"] = clock.split(":");
  return Number(hour) * 60 + Number(minute);
}

function clockValue(minute: number): string {
  return `${String(Math.floor(minute / 60)).padStart(2, "0")}:${String(minute % 60).padStart(2, "0")}`;
}

function sourceLabel(slot: TimeSlot): string {
  return slot.source === "IMPORTED" ? "导入" : "补充";
}

export function SupplementaryTimeSlotEditor({
  projectId,
  document
}: {
  projectId: string;
  document: ResourceRoomProjectDocumentV2;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [feedback, setFeedback] = useState<{
    kind: "success" | "error";
    text: string;
  } | null>(null);
  const mutation = useMutation({
    mutationFn: async ({ type, payload }: { type: string; payload: Record<string, unknown> }) =>
      (await getProjectRepository()).execute(projectId, type, payload),
    onSuccess: async () => {
      setDraft(emptyDraft);
      setFeedback({ kind: "success", text: "补充节次已保存，并已重新检查现有课程安排。" });
      await queryClient.invalidateQueries({
        predicate: (query) => query.queryKey.includes(projectId)
      });
    },
    onError: (error: unknown) =>
      setFeedback({
        kind: "error",
        text: error instanceof Error ? error.message : "节次保存失败"
      })
  });
  const importedCount = document.entities.time_slots.filter(
    (slot) => slot.source === "IMPORTED"
  ).length;
  const slotsFor = (dayPart: "AM" | "PM") =>
    document.entities.time_slots
      .filter((slot) => slot.day_part === dayPart)
      .sort((left, right) => left.sort_order - right.sort_order);

  function edit(slot: TimeSlot): void {
    if (slot.source !== "SUPPLEMENTARY") return;
    setDraft({
      timeSlotId: slot.id,
      dayPart: slot.day_part,
      start: clockValue(slot.start_minute),
      end: clockValue(slot.end_minute)
    });
    setFeedback(null);
  }

  function submit(event: FormEvent): void {
    event.preventDefault();
    const payload = {
      ...(draft.timeSlotId ? { time_slot_id: draft.timeSlotId } : {}),
      day_part: draft.dayPart,
      start_minute: minuteValue(draft.start),
      end_minute: minuteValue(draft.end)
    };
    mutation.mutate({
      type: draft.timeSlotId ? "UPDATE_TIME_SLOT" : "CREATE_TIME_SLOT",
      payload
    });
  }

  function remove(slot: TimeSlot): void {
    if (!window.confirm(`确认删除补充节次“${slot.raw_label}”？`)) return;
    mutation.mutate({
      type: "DELETE_TIME_SLOT",
      payload: { time_slot_id: slot.id }
    });
  }

  return (
    <section aria-labelledby="time-slots-title">
      <div className="resource-section-heading">
        <div>
          <h2 id="time-slots-title">编辑节次</h2>
          <p>导入识别出的节次是学校基线，只读且不可删除；可在其基础上增加补充节次。</p>
        </div>
        <span>{importedCount} 个导入节次 · {document.entities.time_slots.length - importedCount} 个补充节次</span>
      </div>

      {!importedCount ? (
        <div className="resource-errors">请先完成原班课表导入，再添加补充节次。</div>
      ) : (
        <form className="resource-time-slot-form" onSubmit={submit}>
          <label className="field">
            <span>上下午</span>
            <select
              value={draft.dayPart}
              onChange={(event) =>
                setDraft({ ...draft, dayPart: event.target.value as "AM" | "PM" })
              }
            >
              <option value="AM">上午</option>
              <option value="PM">下午</option>
            </select>
          </label>
          <FiveMinuteTimeField
            label="开始时间"
            value={draft.start}
            onChange={(start) => setDraft({ ...draft, start })}
          />
          <FiveMinuteTimeField
            label="结束时间"
            value={draft.end}
            onChange={(end) => setDraft({ ...draft, end })}
          />
          <div className="resource-actions">
            <button className="primary-button" type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? "正在保存…" : draft.timeSlotId ? "保存修改" : "新增补充节次"}
            </button>
            {draft.timeSlotId ? (
              <button
                className="secondary-button"
                type="button"
                disabled={mutation.isPending}
                onClick={() => setDraft(emptyDraft)}
              >
                取消修改
              </button>
            ) : null}
          </div>
        </form>
      )}

      {feedback ? (
        <p
          className={feedback.kind === "success" ? "resource-success" : "resource-errors"}
          role={feedback.kind === "error" ? "alert" : "status"}
        >
          {feedback.text}
        </p>
      ) : null}

      <div className="resource-time-slot-columns">
        {(["AM", "PM"] as const).map((dayPart) => (
          <section className="resource-time-slot-column" key={dayPart}>
            <h3>{dayPart === "AM" ? "上午" : "下午"}</h3>
            {slotsFor(dayPart).length ? (
              <ol>
                {slotsFor(dayPart).map((slot) => (
                  <li key={slot.id}>
                    <div>
                      <strong>{slot.raw_label}</strong>
                      <span className={`resource-slot-source ${slot.source.toLocaleLowerCase()}`}>
                        {sourceLabel(slot)}
                      </span>
                    </div>
                    {slot.source === "SUPPLEMENTARY" ? (
                      <div className="resource-actions">
                        <button
                          className="text-button"
                          type="button"
                          disabled={mutation.isPending}
                          onClick={() => edit(slot)}
                        >
                          修改
                        </button>
                        <button
                          className="text-button danger"
                          type="button"
                          disabled={mutation.isPending}
                          onClick={() => remove(slot)}
                        >
                          删除
                        </button>
                      </div>
                    ) : (
                      <span className="resource-readonly-label">只读</span>
                    )}
                  </li>
                ))}
              </ol>
            ) : (
              <p className="resource-muted">暂无{dayPart === "AM" ? "上午" : "下午"}节次</p>
            )}
          </section>
        ))}
      </div>
    </section>
  );
}
